import {
  collection,
  doc,
  setDoc,
  deleteDoc,
  onSnapshot,
  serverTimestamp,
  type Unsubscribe,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useTaskStore } from '@/stores/taskStore';
import type { Task } from '@/types';
import { toDate, toOptionalDate } from './firestoreDate';
import { logSyncError } from './syncErrors';

function tasksCol(uid: string) {
  return collection(db, 'users', uid, 'tasks');
}

export function startTaskSync(uid: string): Unsubscribe {
  return onSnapshot(
    tasksCol(uid),
    (snap) => {
      const tasks: Task[] = snap.docs.map((d) => {
        const data = d.data();
        const task: Task = {
          id: d.id,
          title: data.title as string,
          status: data.status as Task['status'],
          priority: data.priority as Task['priority'],
          energyRequired: data.energyRequired as Task['energyRequired'],
          durationMinutes: data.durationMinutes as number,
          subtasks: (data.subtasks as Task['subtasks']) ?? [],
          tags: (data.tags as string[]) ?? [],
          createdAt: toDate(data.createdAt),
          updatedAt: toDate(data.updatedAt),
        };

        if (data.notes !== undefined) task.notes = data.notes as string;
        if (data.skipReason != null)
          task.skipReason = data.skipReason as NonNullable<Task['skipReason']>;
        if (data.routineId !== undefined) task.routineId = data.routineId as string;
        if (data.habitId !== undefined) task.habitId = data.habitId as string;
        if (data.calendarEventId !== undefined)
          task.calendarEventId = data.calendarEventId as string;
        if (data.color !== undefined) task.color = data.color as string;
        if (data.icon !== undefined) task.icon = data.icon as string;
        if (data.microStep != null)
          task.microStep = data.microStep as NonNullable<Task['microStep']>;

        const scheduledStart = toOptionalDate(data.scheduledStart);
        if (scheduledStart !== undefined) task.scheduledStart = scheduledStart;

        const scheduledEnd = toOptionalDate(data.scheduledEnd);
        if (scheduledEnd !== undefined) task.scheduledEnd = scheduledEnd;

        const completedAt = toOptionalDate(data.completedAt);
        if (completedAt !== undefined) task.completedAt = completedAt;

        const skippedAt = toOptionalDate(data.skippedAt);
        if (skippedAt !== undefined) task.skippedAt = skippedAt;

        return task;
      });
      useTaskStore.setState({ tasks });
    },
    logSyncError('taskSync', uid),
  );
}

export async function writeTask(uid: string, task: Task): Promise<void> {
  const { id, ...rest } = task;
  await setDoc(doc(tasksCol(uid), id), { ...rest, serverUpdatedAt: serverTimestamp() });
}

export async function deleteTask(uid: string, taskId: string): Promise<void> {
  await deleteDoc(doc(tasksCol(uid), taskId));
}
